/**
 * Screen 31 — Conversation thread. Visual structure mirrors
 * Triviana_Wireframes.html screen 31: an appbar with the peer's
 * avatar + presence, a scrollable message area with in/out bubbles,
 * and a rounded composer pinned to the bottom.
 *
 * Real-time:
 *   * `/ws/messages/{conv_id}` — new messages append; the other side's
 *     read tick renders on the last outgoing bubble; typing.start/stop
 *     drives a footer "typing…" indicator.
 *   * On mount we mark the thread read so the bell + inbox badge drop.
 *
 * Send: idempotency uuid generated client-side; optimistic bubble
 * inserted first, reconciled on the server response. Live-play lock
 * flips the composer into a warning card.
 *
 * Compose mode: /messages/new/<user_id> renders an empty thread with
 * the composer wired to that user id. First successful send swaps
 * history into /messages/<conv_id> and re-renders the full view.
 */

import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { I, icon } from '../components/icons.js';
import {
  blockUser,
  fetchThread,
  markThreadRead,
  messageClientUuid,
  muteConversation,
  openThreadSocket,
  reportMessage,
  sendMessage,
  unmuteConversation,
} from '../lib/messaging.js';

let _socket = null;
let _typingTimer = null;
let _typingBroadcast = false;


function conversationIdFromUrl() {
  const m = /^\/messages\/([^/]+)$/.exec(window.location.pathname);
  if (!m) return null;
  const seg = decodeURIComponent(m[1]);
  if (seg === 'new') return null;
  return seg;
}

function composeTargetFromUrl() {
  const m = /^\/messages\/new\/([^/]+)$/.exec(window.location.pathname);
  return m ? decodeURIComponent(m[1]) : null;
}


function initials(name) {
  return (name || '?')[0].toUpperCase();
}


function shell(peerName) {
  return `
    <div class="appbar">
      <a href="/messages" data-link class="appbar-back" aria-label="Back">‹</a>
      <div style="position:relative;flex:none;">
        <div class="av" id="conv-peer-av" style="width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#fff;background:#2A2140;">${escape(initials(peerName))}</div>
      </div>
      <div class="grow" style="margin-left:2px;">
        <div id="conv-title" style="font-size:14px;color:#fff;font-weight:600;font-family:var(--font-display);">${escape(peerName || 'Chat')}</div>
        <div id="conv-subtitle" style="font-size:10px;color:var(--mute);font-weight:500;"></div>
      </div>
      <button type="button" id="conv-kebab" class="ic" style="cursor:pointer;background:transparent;border:none;color:var(--ink-2);font-size:20px;">⋮</button>
    </div>
    <div id="conv-lock-banner" class="card" style="display:none;border-color:#5A431A;background:#241B0E;padding:11px;margin:11px 18px 0;">
      <div class="row">
        <div class="ic" style="background:#3A2A12;color:#F7C667;">${icon(I.lock, { size: 16 })}</div>
        <div class="grow">
          <b style="font-size:12.5px;color:#F7C667;font-weight:600;">Messages are locked during live play</b>
          <div style="font-size:11px;color:#C9B27E;font-weight:500;margin-top:1px;">Chat unlocks when your game ends.</div>
        </div>
      </div>
    </div>
    <div id="conv-messages" class="pad" style="padding-bottom:96px;overflow-y:auto;flex:1;">
      <div class="muted center" style="padding:24px;font-size:12px;">Loading…</div>
    </div>
    <div id="conv-typing" style="display:none;padding:0 18px 6px;font-size:11px;color:var(--mute);font-style:italic;"></div>
    <form id="conv-form" novalidate style="position:absolute;left:0;right:0;bottom:0;padding:10px 14px 14px;background:linear-gradient(180deg,rgba(20,16,31,0),#14101F 25%);">
      <div class="row" style="gap:8px;background:#1C1730;border:1px solid #2E2748;border-radius:22px;padding:8px 10px;">
        <textarea id="conv-input" rows="1" maxlength="500"
          placeholder="Message… (text & emoji only)"
          style="flex:1;background:transparent;border:none;outline:none;color:var(--ink);font-size:13px;font-weight:500;font-family:inherit;resize:none;max-height:120px;line-height:1.4;"></textarea>
        <button type="submit" id="conv-send" aria-label="Send"
          style="width:34px;height:34px;border-radius:17px;background:var(--teal);color:#04211C;display:flex;align-items:center;justify-content:center;border:none;cursor:pointer;flex:none;">${icon(I.send, { size: 15 })}</button>
      </div>
    </form>
    <div id="conv-kebab-menu" class="card" style="display:none;position:absolute;top:56px;right:16px;padding:6px;min-width:180px;z-index:5;box-shadow:var(--shadow-card);">
      <button type="button" data-menu="profile" class="btn ghost sm" style="width:100%;margin:2px 0;text-align:left;padding:9px 12px;">View profile</button>
      <button type="button" data-menu="mute" class="btn ghost sm" style="width:100%;margin:2px 0;text-align:left;padding:9px 12px;">Mute conversation</button>
      <button type="button" data-menu="unmute" class="btn ghost sm" style="width:100%;margin:2px 0;text-align:left;padding:9px 12px;">Unmute</button>
      <button type="button" data-menu="report" class="btn ghost sm" style="width:100%;margin:2px 0;text-align:left;padding:9px 12px;color:#F7C667;">Report</button>
      <button type="button" data-menu="block" class="btn ghost sm" style="width:100%;margin:2px 0;text-align:left;padding:9px 12px;color:#FF8FB6;">Block user</button>
    </div>
  `;
}


function bubbleHtml(message, meId) {
  const isMine = message.sender_id === meId;
  const time = formatTime(message.created_at);
  const status = message.status || 'delivered';
  if (isMine) {
    return `
      <div style="display:flex;justify-content:flex-end;margin-bottom:8px;"
           data-message-id="${escape(message.id)}" data-status="${escape(status)}">
        <div class="bubble bubble--out"
             style="max-width:74%;background:#15323A;border:1px solid #1E6E5C;border-radius:14px 14px 4px 14px;padding:9px 12px;">
          <div class="bubble__body" style="font-size:13px;color:#EAFBF6;font-weight:500;line-height:1.5;">${escape(message.body)}</div>
          <div class="row" style="gap:4px;justify-content:flex-end;margin-top:3px;">
            <span style="font-size:9px;color:#7FC3B2;font-weight:500;">${escape(time)}</span>
            ${status === 'held' ? '<span style="font-size:9px;color:#F7C667;margin-left:3px;">· Held for review</span>' : ''}
            <span class="bubble__ticks" style="font-size:10px;color:#5FE0BE;margin-left:2px;"></span>
          </div>
        </div>
      </div>
    `;
  }
  return `
    <div style="display:flex;margin-bottom:8px;"
         data-message-id="${escape(message.id)}" data-status="${escape(status)}">
      <div class="bubble bubble--in"
           style="max-width:74%;background:#241C44;border:1px solid #3A2F5F;border-radius:14px 14px 14px 4px;padding:9px 12px;">
        <div class="bubble__body" style="font-size:13px;color:#EDE9F7;font-weight:500;line-height:1.5;">${escape(message.body)}</div>
        <div style="font-size:9px;color:var(--mute);font-weight:500;margin-top:3px;">${escape(time)}</div>
      </div>
    </div>
  `;
}


function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}


function scrollToBottom(container) {
  container.scrollTop = container.scrollHeight;
}


function appendMessage(container, message, meId) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = bubbleHtml(message, meId);
  const el = wrapper.firstElementChild;
  container.appendChild(el);
  scrollToBottom(container);
  return el;
}


async function paintThread(container, convId, meId) {
  try {
    const res = await fetchThread(convId);
    if (!res.items.length) {
      container.innerHTML = `<div class="muted center" style="padding:32px 0;font-size:12px;">No messages yet — say hi.</div>`;
      return { peerId: null };
    }
    container.innerHTML = res.items.map((m) => bubbleHtml(m, meId)).join('');
    scrollToBottom(container);
    const first = res.items.find((m) => m.sender_id !== meId);
    return { peerId: first ? first.sender_id : null };
  } catch (err) {
    if (err && err.status === 404) {
      container.innerHTML = `<div class="card center" style="padding:20px;"><b style="color:#F7C667;font-size:12.5px;">Conversation not found</b></div>`;
    } else {
      container.innerHTML = `
        <div class="card center" style="padding:20px;">
          <b style="font-size:12.5px;color:#F7C667;font-weight:600;">Couldn't load messages</b>
          <div class="muted" style="font-size:11px;margin-top:4px;">${escape(err.message || 'Try again in a moment.')}</div>
        </div>
      `;
    }
    console.error('thread load failed', err);
    return { peerId: null };
  }
}


function bindPeerToForm(form, peerId) {
  if (peerId) form.dataset.toUserId = peerId;
}


function wireComposer(root, convId, meId) {
  const form = root.querySelector('#conv-form');
  const input = root.querySelector('#conv-input');
  const container = root.querySelector('#conv-messages');
  const lockBanner = root.querySelector('#conv-lock-banner');

  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
    if (!_typingBroadcast && _socket) {
      _socket.startTyping();
      _typingBroadcast = true;
    }
    if (_typingTimer) clearTimeout(_typingTimer);
    _typingTimer = setTimeout(() => {
      if (_socket) _socket.stopTyping();
      _typingBroadcast = false;
    }, 3000);
  });

  // Send on Enter, newline on Shift-Enter.
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      form.requestSubmit();
    }
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const body = input.value.trim();
    if (!body) return;
    const clientUuid = messageClientUuid();
    const optimistic = appendMessage(
      container,
      {
        id: `optimistic-${clientUuid}`,
        sender_id: meId,
        body,
        created_at: new Date().toISOString(),
        status: 'delivered',
      },
      meId,
    );
    optimistic.dataset.status = 'sending';
    input.value = '';
    input.style.height = 'auto';
    if (_typingBroadcast && _socket) _socket.stopTyping();
    _typingBroadcast = false;

    const toUserId = form.dataset.toUserId || null;
    if (!toUserId) {
      optimistic.dataset.status = 'error';
      toast('Cannot resolve recipient — try reloading.');
      return;
    }

    try {
      const res = await sendMessage({ toUserId, body, clientUuid });
      const { message, conversation_id: newConvId } = res;
      optimistic.dataset.messageId = message.id;
      optimistic.dataset.status = message.status;
      if (message.status === 'held') {
        const meta = optimistic.querySelector('div[style*="justify-content:flex-end"]');
        if (meta && !meta.querySelector('.bubble__held')) {
          meta.insertAdjacentHTML(
            'beforeend',
            '<span class="bubble__held" style="font-size:9px;color:#F7C667;margin-left:3px;">· Held for review</span>',
          );
        }
      }
      lockBanner.style.display = 'none';
      if (!convId && newConvId) {
        history.replaceState(null, '', `/messages/${newConvId}`);
        renderConversation(root);
      }
    } catch (err) {
      optimistic.dataset.status = 'error';
      if (err && err.code === 'live_play_locked') {
        lockBanner.style.display = '';
      } else {
        toast(err && err.message ? err.message : "Couldn't send");
      }
    }
  });
}


async function wireSocket(root, convId, meId) {
  const container = root.querySelector('#conv-messages');
  const typingEl = root.querySelector('#conv-typing');

  _socket = openThreadSocket(convId, {
    onMessageNew: (message) => {
      if (message.sender_id === meId) {
        const existing = container.querySelector(
          `[data-message-id="${CSS.escape(message.id)}"]`,
        );
        if (existing) return;
      }
      appendMessage(container, message, meId);
      if (message.sender_id !== meId) {
        markThreadRead(convId).catch(() => {});
      }
    },
    onMessageRead: () => {
      const bubbles = container.querySelectorAll('.bubble--out');
      const last = bubbles[bubbles.length - 1];
      if (last) {
        const ticks = last.querySelector('.bubble__ticks');
        if (ticks && !ticks.textContent) ticks.textContent = '✓✓';
      }
    },
    onTyping: ({ type }) => {
      if (type === 'typing.start') {
        typingEl.style.display = '';
        typingEl.textContent = 'typing…';
      } else {
        typingEl.style.display = 'none';
      }
    },
  });
}


function wireKebab(root, convId, peerId) {
  const button = root.querySelector('#conv-kebab');
  const menu = root.querySelector('#conv-kebab-menu');
  button.addEventListener('click', (e) => {
    e.stopPropagation();
    menu.style.display = menu.style.display === 'none' ? '' : 'none';
  });
  document.addEventListener('click', (e) => {
    if (menu && !menu.contains(e.target) && e.target !== button) {
      menu.style.display = 'none';
    }
  });
  menu.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-menu]');
    if (!btn) return;
    menu.style.display = 'none';
    const action = btn.dataset.menu;
    try {
      if (action === 'profile') {
        if (!peerId) return toast('No profile linked to this thread');
        navigate(`/u/${peerId}`);
      } else if (action === 'mute') {
        if (!convId) return;
        await muteConversation(convId, null);
        toast('Muted');
      } else if (action === 'unmute') {
        if (!convId) return;
        await unmuteConversation(convId);
        toast('Unmuted');
      } else if (action === 'block') {
        if (!peerId) return toast('No user to block');
        if (!confirm('Block this user? They will not be able to reach you.')) return;
        await blockUser(peerId);
        toast('Blocked');
        navigate('/messages');
      } else if (action === 'report') {
        const reason = promptReason();
        if (!reason) return;
        const lastInbound = root.querySelector('.bubble--in');
        if (!lastInbound) return toast('No message to report');
        const messageId = lastInbound.parentElement.dataset.messageId;
        await reportMessage(messageId, reason);
        toast('Reported. Thanks — our team will review.');
      }
    } catch (err) {
      toast(err && err.message ? err.message : 'Action failed');
    }
  });
}


function promptReason() {
  const raw = prompt(
    'Report reason — one of: spam, scam, harassment, sexual, other',
    'spam',
  );
  if (!raw) return null;
  const r = raw.trim().toLowerCase();
  if (!['spam', 'scam', 'harassment', 'sexual', 'other'].includes(r)) {
    toast('Unknown reason');
    return null;
  }
  return r;
}


function cleanup() {
  if (_typingTimer) {
    clearTimeout(_typingTimer);
    _typingTimer = null;
  }
  if (_socket) {
    _socket.close();
    _socket = null;
  }
  _typingBroadcast = false;
}


export async function renderConversation(root) {
  cleanup();
  if (!auth.isAuthenticated()) {
    navigate('/onboarding');
    return;
  }
  const convId = conversationIdFromUrl();
  const composeTo = composeTargetFromUrl();
  if (!convId && !composeTo) {
    navigate('/messages');
    return;
  }
  const me = auth.getMe();
  const meId = me && me.id;
  root.innerHTML = shell('Chat');
  window.addEventListener('popstate', cleanup, { once: true });

  if (composeTo) {
    const container = root.querySelector('#conv-messages');
    container.innerHTML = `<div class="muted center" style="padding:32px 0;font-size:12px;">Start a new conversation.</div>`;
    bindPeerToForm(root.querySelector('#conv-form'), composeTo);
    wireComposer(root, null, meId);
    wireKebab(root, null, composeTo);
    return;
  }

  const container = root.querySelector('#conv-messages');
  const { peerId } = await paintThread(container, convId, meId);
  bindPeerToForm(root.querySelector('#conv-form'), peerId);
  wireComposer(root, convId, meId);
  await wireSocket(root, convId, meId);
  wireKebab(root, convId, peerId);
  markThreadRead(convId).catch(() => {});
}
